import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { presenceAPI } from '../../lib/api';
import { cn, getInitials } from '../../lib/utils';
import {
    Circle,
    Clock,
    Moon,
    MinusCircle,
    X,
    Check,
    Edit2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';

const statusOptions = [
    { value: 'online', label: 'Online', icon: Circle, color: 'text-green-500', bgColor: 'bg-green-500' },
    { value: 'away', label: 'Away', icon: Clock, color: 'text-yellow-500', bgColor: 'bg-yellow-500' },
    { value: 'busy', label: 'Jangan Ganggu', icon: MinusCircle, color: 'text-red-500', bgColor: 'bg-red-500' },
    { value: 'offline', label: 'Offline', icon: Moon, color: 'text-gray-400', bgColor: 'bg-gray-400' },
];

const UserStatusSelector = ({ className }) => {
    const { currentWorkspace } = useWorkspace();
    const { user } = useAuth();
    const [currentStatus, setCurrentStatus] = useState('online');
    const [customStatus, setCustomStatus] = useState('');
    const [tempCustomStatus, setTempCustomStatus] = useState('');
    const [isEditingCustom, setIsEditingCustom] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    // Load current status
    useEffect(() => {
        if (currentWorkspace) {
            loadPresence();
        }
    }, [currentWorkspace]);

    const loadPresence = async () => {
        try {
            const res = await presenceAPI.getWorkspacePresence(currentWorkspace.id);
            const myPresence = res.data?.find(p => p.user_id === user?.id);
            if (myPresence) {
                setCurrentStatus(myPresence.status || 'online');
                setCustomStatus(myPresence.custom_status || '');
            }
        } catch (error) {
            console.error('Failed to load presence:', error);
        }
    };

    const updateStatus = async (status, custom = null) => {
        try {
            await presenceAPI.updateMyPresence(currentWorkspace.id, status, custom);
            setCurrentStatus(status);
            if (custom !== null) {
                setCustomStatus(custom);
            }
            toast.success('Status diperbarui');
        } catch (error) {
            toast.error('Gagal memperbarui status');
        }
    };

    const handleSaveCustomStatus = () => {
        updateStatus(currentStatus, tempCustomStatus);
        setIsEditingCustom(false);
    };

    const handleClearCustomStatus = () => {
        updateStatus(currentStatus, '');
        setTempCustomStatus('');
    };

    const currentStatusOption = statusOptions.find(s => s.value === currentStatus) || statusOptions[0];
    const StatusIcon = currentStatusOption.icon;

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <button className={cn(
                    'flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors text-sm',
                    className
                )}>
                    <div className="relative">
                        <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-white text-xs font-medium">
                            {getInitials(user?.name || 'U')}
                        </div>
                        <span className={cn(
                            'absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white',
                            currentStatusOption.bgColor
                        )} />
                    </div>
                    <div className="text-left hidden sm:block">
                        <p className="font-medium text-white text-sm truncate max-w-[120px]">
                            {user?.name || 'User'}
                        </p>
                        {customStatus ? (
                            <p className="text-xs text-white/60 truncate max-w-[120px]">
                                {customStatus}
                            </p>
                        ) : (
                            <p className={cn('text-xs flex items-center gap-1', currentStatusOption.color)}>
                                <StatusIcon className="w-2.5 h-2.5" fill="currentColor" />
                                {currentStatusOption.label}
                            </p>
                        )}
                    </div>
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="font-normal">
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <div className="w-10 h-10 rounded-full bg-brand-primary flex items-center justify-center text-white font-medium">
                                {getInitials(user?.name || 'U')}
                            </div>
                            <span className={cn(
                                'absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white',
                                currentStatusOption.bgColor
                            )} />
                        </div>
                        <div>
                            <p className="font-semibold text-gray-800">{user?.name}</p>
                            <p className="text-xs text-gray-500">{user?.email}</p>
                        </div>
                    </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                {/* Custom Status */}
                <div className="px-2 py-2">
                    {isEditingCustom ? (
                        <div className="flex items-center gap-2">
                            <Input
                                value={tempCustomStatus}
                                onChange={(e) => setTempCustomStatus(e.target.value)}
                                placeholder="Apa yang sedang kamu kerjakan?"
                                className="h-8 text-sm"
                                autoFocus
                            />
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={handleSaveCustomStatus}>
                                <Check className="w-4 h-4 text-green-600" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setIsEditingCustom(false)}>
                                <X className="w-4 h-4 text-gray-400" />
                            </Button>
                        </div>
                    ) : (
                        <button
                            onClick={() => {
                                setTempCustomStatus(customStatus);
                                setIsEditingCustom(true);
                            }}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-100 text-sm text-left"
                        >
                            <Edit2 className="w-4 h-4 text-gray-400" />
                            {customStatus ? (
                                <span className="flex-1 truncate">{customStatus}</span>
                            ) : (
                                <span className="text-gray-400">Set status kustom</span>
                            )}
                        </button>
                    )}
                    {customStatus && !isEditingCustom && (
                        <button
                            onClick={handleClearCustomStatus}
                            className="w-full flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-100 text-xs text-gray-500 mt-1"
                        >
                            <X className="w-3 h-3" />
                            Hapus status kustom
                        </button>
                    )}
                </div>

                <DropdownMenuSeparator />

                {/* Status Options */}
                {statusOptions.map((status) => {
                    const Icon = status.icon;
                    return (
                        <DropdownMenuItem
                            key={status.value}
                            onClick={() => updateStatus(status.value)}
                            className="flex items-center gap-3 cursor-pointer"
                        >
                            <Icon className={cn('w-4 h-4', status.color)} fill={status.value === 'online' ? 'currentColor' : 'none'} />
                            <span>{status.label}</span>
                            {currentStatus === status.value && (
                                <Check className="w-4 h-4 ml-auto text-brand-primary" />
                            )}
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default UserStatusSelector;
